import { format } from 'date-fns';
import { Send, Trash2 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { type InviteCode, inviteCodeStore } from '@/stores';
import { highlightKeyword } from '@/utils/helpers';

interface InviteCodeTableProps {
  onDelete: (id: string) => void;
  onSend: (row: InviteCode) => void;
}

export const InviteCodeTable = observer(
  ({ onDelete, onSend }: InviteCodeTableProps) => {
    const keyword = inviteCodeStore.searchKeyword;

    const columns: Column<InviteCode>[] = [
      {
        id: 'code',
        header: '邀请码',
        accessor: (row) => (
          <span className="font-mono font-medium">
            {highlightKeyword(row.code, keyword)}
          </span>
        ),
      },
      {
        id: 'status',
        header: '状态',
        accessor: (row) => {
          const isUsed =
            row.status === 'used' || Boolean(row.usedAt) || Boolean(row.usedBy);
          const statusMap = {
            active: { label: '可用', variant: 'success' as const },
            used: { label: '已使用', variant: 'secondary' as const },
            expired: { label: '已过期', variant: 'destructive' as const },
            deleted: { label: '已删除', variant: 'outline' as const },
          };
          const effectiveStatus = isUsed ? 'used' : row.status;
          const status = statusMap[effectiveStatus] ?? statusMap.active;
          return (
            <span className="font-semibold">
              <Badge variant={status.variant}>{status.label}</Badge>
            </span>
          );
        },
      },
      {
        id: 'creatorUid',
        header: '创建者',
        accessor: (row) =>
          row.creatorUid ? highlightKeyword(row.creatorUid, keyword) : '-',
      },
      {
        id: 'createdAt',
        header: '创建时间',
        accessor: (row) =>
          format(new Date(row.createdAt), 'yyyy-MM-dd HH:mm:ss'),
      },
      {
        id: 'expiresAt',
        header: '到期时间',
        accessor: (row) => {
          const isUnused = !row.usedAt && !row.usedBy;
          const isExpired =
            row.expiresAt && new Date(row.expiresAt) < new Date();
          const isActive =
            row.expiresAt && new Date(row.expiresAt) >= new Date() && isUnused;
          const text = row.expiresAt
            ? format(new Date(row.expiresAt), 'yyyy-MM-dd HH:mm:ss')
            : '-';
          const colorClass =
            isExpired && isUnused
              ? 'text-destructive font-medium'
              : isActive
                ? 'text-green-600 font-medium'
                : undefined;
          return <span className={colorClass}>{text}</span>;
        },
      },
      {
        id: 'usedAt',
        header: '使用时间',
        accessor: (row) =>
          row.usedAt
            ? format(new Date(row.usedAt), 'yyyy-MM-dd HH:mm:ss')
            : '-',
      },
      {
        id: 'usedBy',
        header: '使用者',
        accessor: (row) =>
          row.usedBy ? highlightKeyword(row.usedBy, keyword) : '-',
      },
      {
        id: 'actions',
        header: '操作',
        accessor: (row) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSend(row)}
              disabled={
                row.status !== 'active' ||
                Boolean(row.usedAt) ||
                Boolean(row.usedBy)
              }
              title="发送/重发到邮箱"
            >
              <Send className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(row.id)}
              disabled={row.status === 'used' || row.status === 'deleted'}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ];

    return (
      <DataTable
        columns={columns}
        data={inviteCodeStore.inviteCodes}
        loading={inviteCodeStore.loading}
        emptyMessage="暂无邀请码"
      />
    );
  },
);
